import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CmsEntities } from 'client/data/models/cms';
import { TrackingHandler } from 'client/tracking/handler';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { Share } from 'site-modules/shared/components/share/share';
import { HERO_PHOTO_SIZE, LOGO_SOURCE, TOP_RATED_BREADCRUMBS } from 'site-modules/shared/constants/top-rated/top-rated';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { AwardsAccolades } from 'site-modules/editorial/components/top-rated/awards-accolades/2020-2022/awards-accolades';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { rendererTopRatedIndex } from 'site-modules/editorial/pages/top-rated/top-rated-renderer';
import dateFormat from 'dateformat';
import { HeroContentCurve } from 'site-modules/editorial/components/hero-content-curve/hero-content-curve';
import { CurveOverFooter } from 'site-modules/editorial/components/top-rated/eve-curve/eve-curve-footer';
import { topRatedHelpers } from 'site-modules/shared/utils/top-rated/top-rated-helpers';

import { Winners } from './components/winners/winners';
import { CategoryLinks } from './components/category-links/category-links';

import './styles/top-rated.scss';

export function TopRatedUI({ content = null, pathname = null, isMobile = false, year }) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoModalData, setVideoModalData] = useState({});

  useEffect(() => {
    TrackingHandler.useMap({ ...EditorialMap });
  }, []);

  const onToggleVideoModal = useCallback(
    videoData => {
      setIsVideoModalOpen(!isVideoModalOpen);
      topRatedHelpers.toggleBodyModalClass('light-theme', !isVideoModalOpen);

      if (!isVideoModalOpen) {
        setVideoModalData(videoData);
      }
    },
    [isVideoModalOpen]
  );

  const { published, updated, title: contentTitle, subtitle } = content;

  const mainEntries = content.child('main').children();
  const permissions = content.child('permissions');
  const canonical = getCanonical(content);

  const vehicleCardEntries = mainEntries.reduce((acc, entry) => {
    if (entry.metadata('renderType').value() === 'card') {
      acc.push(entry);
    }
    return acc;
  }, []);

  const logoSource = LOGO_SOURCE[year];

  // hero content
  const heroContentEntry = content.child('hero-content');
  const title = heroContentEntry.title || contentTitle;
  const heroContentProps = {
    entry: heroContentEntry,
    alt: '',
    heroSize: isMobile ? HERO_PHOTO_SIZE.MOBILE : HERO_PHOTO_SIZE.DESKTOP,
    isMobile,
  };
  const cssVideoProps = {};
  cssVideoProps['--object-position'] = heroContentEntry.metadata('videoPosition').value() || null;

  const breadcrumbsData = TOP_RATED_BREADCRUMBS.concat({
    title,
    href: pathname,
  });

  return (
    <main
      id="main-content"
      role="main"
      className={`top-rated landing-page top-rated-${year} pos-r`}
      style={cssVideoProps}
    >
      <Breadcrumbs data={breadcrumbsData} className="px-0 mb-1_5 mb-md-1 text-white text-shadow-contrast" />
      {isMobile ? (
        <FeatureFlag name="disableTopRatedHeroVideo">
          {enabled =>
            enabled ? (
              <section className="hero-content pos-r text-center">
                <header className="hero-content-caption pos-a">
                  <img
                    src={getStaticImageUrl(logoSource)}
                    alt={`${heroContentProps.alt} logo`}
                    width={208}
                    height={51}
                  />
                </header>
                <article className="video-placeholder" aria-hidden style={{ minHeight: '536px' }}>
                  Video Placeholeder
                </article>
              </section>
            ) : (
              <HeroContentCurve
                content={heroContentEntry}
                isMobile={isMobile}
                contentTitle={contentTitle}
                showH1Content={false}
                showContentBgSwoop={false}
              />
            )
          }
        </FeatureFlag>
      ) : (
        <HeroContentCurve
          content={heroContentEntry}
          isMobile={isMobile}
          contentTitle={contentTitle}
          showH1Content={false}
          showContentBgSwoop={false}
        />
      )}
      <article className="container text-center">
        <h1 className="heading-2 mb-1_5">
          <span className="visually-hidden">{title}</span>
          <img
            src={getStaticImageUrl(logoSource)}
            alt=""
            width={isMobile ? '286' : '530'}
            className="mx-auto mt-0_75"
            style={{ maxWidth: '98%' }}
          />
        </h1>
        <div className="text-cool-gray-30 mb-1_5 px-1 fw-medium">
          <span className="hidden-md-down size-30">{subtitle}</span>
          <span className="hidden-md-up size-24">{subtitle}</span>
        </div>
        <div className="size-20 text-cool-gray-30">{dateFormat(updated || published, 'mmmm dS, yyyy')}</div>
      </article>
      <CategoryLinks vehicleCardEntries={vehicleCardEntries} />
      <CurveOverFooter isMobile={isMobile} />
      <Winners vehicleCardEntries={vehicleCardEntries} isMobile={isMobile} onToggleVideoModal={onToggleVideoModal} />
      <Share
        year={year}
        url={canonical}
        title={title}
        labelClassName="d-none"
        containerClassName="d-block text-center py-3 pos-r container"
        iconClassName="heading-4 mx-1 p-0 text-cool-gray-100"
      />
      {permissions && (
        <section className="container">
          <AwardsAccolades
            title={permissions.metadata('title').value()}
            content={permissions.metadata('content').value()}
            mailTo={permissions.metadata('mailTo').value()}
            email={permissions.metadata('email').value()}
            containerClassName="px-0_75 text-cool-gray-100"
            anchorClassName="text-white"
          />
        </section>
      )}

      <YoutubeVideoModalAsync
        onToggleModal={onToggleVideoModal}
        isOpen={isVideoModalOpen}
        videoId={videoModalData.videoId}
        videoAspectRatio={videoModalData.videoAspectRatio}
        creativeId={videoModalData.creativeId}
        videoTitle={videoModalData.videoTitle}
      />
    </main>
  );
}

TopRatedUI.propTypes = {
  content: CmsEntities.Content,
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
  year: PropTypes.string.isRequired,
};

export const TopRated = rendererTopRatedIndex(TopRatedUI);
